import { DateTimePreferencesService } from '@core/services/date-time-preferences.service';
import { Component, inject, ChangeDetectionStrategy, OnInit, signal, computed, HostListener, viewChild, ElementRef, NgZone, PLATFORM_ID, DestroyRef } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { interval, filter, map } from 'rxjs';
import { userDisplayName, primaryRoleLabel } from '@core/utils/user-identity.utils';
import { isPlatformBrowser } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { MenuService } from '@core/services/menu.service';
import { CommandPaletteService } from '@core/services/command-palette.service';
import { AuthService } from '@core/services/auth.service';
import { WebSocketService } from '@core/services/websocket.service';
import { TradingModeService } from '@core/services/trading-mode.service';
import { PaperTradingBadgeComponent } from '@shared/components/paper-trading-badge/paper-trading-badge';
import { NotificationCenterComponent } from '../notification-center/notification-center.component';
import { LanguageSelectorComponent } from '@shared/components/language-selector/language-selector.component';
import { GlobalWebSocketStatusComponent } from '@shared/components/global-websocket-status/global-websocket-status.component';
import { IconComponent } from '@shared/components/ui/icon';
import { environment } from '@environments/environment';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [
    RouterModule,
    PaperTradingBadgeComponent,
    NotificationCenterComponent,
    LanguageSelectorComponent,
    GlobalWebSocketStatusComponent,
    IconComponent
  ],
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NavbarComponent implements OnInit {
  private router = inject(Router);
  readonly dateTime = inject(DateTimePreferencesService);
  private menuService = inject(MenuService);
  readonly mobileMenuOpen = this.menuService.showMobileMenu;
  private authService = inject(AuthService);
  private wsService = inject(WebSocketService);
  private tradingMode = inject(TradingModeService);
  private ngZone = inject(NgZone);
  private palette = inject(CommandPaletteService);
  private platformId = inject(PLATFORM_ID);
  private destroyRef = inject(DestroyRef);
  private breakpoints = inject(BreakpointObserver);

  /** Tailwind `md` breakpoint — keep in sync with the `md:` utilities in the template. */
  private static readonly MD_QUERY = '(min-width: 768px)';

  /**
   * RF-QA-195 · True at the Tailwind `md` breakpoint and up. The paper-trading
   * badge (a `role=status` live region) and the mobile connection dot used to
   * be rendered TWICE (desktop + mobile twins hidden by CSS only), so screen
   * readers announced "PAPER TRADING updated …" two times. Rendering exactly
   * one variant per breakpoint via @if keeps a single live region in the DOM.
   */
  readonly isMdUp = toSignal(
    this.breakpoints.observe(NavbarComponent.MD_QUERY).pipe(map((state) => state.matches)),
    { initialValue: this.breakpoints.isMatched(NavbarComponent.MD_QUERY) },
  );

  /** True when system is in LIVE mode (real money). Drives the navbar red rule. */
  readonly isLiveMode = computed(() => !this.tradingMode.isPaperTrading());

  private profileMenuRef = viewChild<ElementRef<HTMLDivElement>>('profileMenu');

  /** Platform-aware keyboard shortcut modifier key for display */
  readonly shortcutModifier: string = (() => {
    if (!isPlatformBrowser(this.platformId)) { return 'Ctrl+'; }
    // Prefer User-Agent Client Hints (Chromium), fall back to deprecated navigator.platform.
    // GAP-FE-A01 r106 (Opus-Max v74): Navigator.userAgentData typed
    // via `src/types/browser-extensions.d.ts` — no `as any` needed.
    const platform = navigator.userAgentData?.platform
      ?? navigator.platform
      ?? '';
    return platform.toUpperCase().includes('MAC') ? '\u2318' : 'Ctrl+';
  })();

  readonly multiLanguageEnabled = environment.features.multiLanguageSupport;

  showProfileMenu = signal(false);

  // Bloomberg Terminal Pro: Live clock
  currentTime = signal(new Date());

  // Connection status — derived from WebSocketService.
  // Note: reflects WebSocket transport state, not full backend health.
  // A dedicated health-endpoint poll would give more accurate "SYS" status;
  // see SystemHealthWebSocketService for a richer health feed.
  isConnected = computed(() => this.wsService.connectionStatus() === 'connected');
  systemStatus = computed<'online' | 'degraded' | 'offline'>(() => {
    switch (this.wsService.connectionStatus()) {
      case 'connected': return 'online';
      case 'connecting': return 'degraded';
      case 'disconnected':
      case 'error':
      default: return 'offline';
    }
  });

  // User identity — derived from AuthService
  userDisplayName = computed(() => userDisplayName(this.authService.currentUser()));
  userInitial = computed(() => this.userDisplayName().charAt(0).toUpperCase());
  userEmail = computed(() => this.authService.currentUser()?.email ?? '');
  userRole = computed(() => primaryRoleLabel(this.authService.currentUser()?.roles) ?? 'User');

  ngOnInit(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.showProfileMenu.set(false));
    // GAP-FE-038: RxJS interval() + takeUntilDestroyed replaces manual
    // setInterval / clearInterval — cleanup is now tied to the DestroyRef
    // lifetime so we can't leak the clock callback after a router teardown.
    // Still run outside Angular zone so the 1 Hz tick doesn't trigger global
    // change detection; the signal write below schedules targeted OnPush CD
    // for this component only.
    this.ngZone.runOutsideAngular(() => {
      interval(1000)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.currentTime.set(new Date()));
    });
  }

  toggleMobileMenu(): void {
    this.menuService.toggleMobileMenu();
  }

  toggleProfileMenu(): void {
    this.showProfileMenu.update(v => !v);
  }

  logout(): void {
    this.authService.logout();
  }

  /**
   * The header button is labelled "Search ⌘K", so clicking it must do the SAME
   * thing as pressing ⌘K — open the command palette. It previously routed to
   * the /search page, a different surface than its own hotkey hint advertised.
   * The deep-search page stays reachable via the palette ("Global Search").
   */
  openSearch(): void {
    this.palette.open();
  }

  @HostListener('document:keydown', ['$event'])
  onGlobalKeydown(event: KeyboardEvent): void {
    // NOTE: ⌘K / Ctrl+K is owned by the global command palette (registered in
    // AppComponent via HotkeyService → CommandPaletteService.toggle()). This
    // navbar previously ALSO bound ⌘K → navigate-to-/search, so a single
    // keypress both opened the palette AND routed to /search — they stomped
    // each other and the palette was unusable ("search doesn't work"). The ⌘K
    // binding is removed here so the palette owns the shortcut; the header
    // Search button now opens the palette too (see openSearch).
    if (event.key === 'Escape' && this.showProfileMenu()) {
      this.showProfileMenu.set(false);
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const ref = this.profileMenuRef();
    if (this.showProfileMenu() && ref && !ref.nativeElement.contains(event.target as Node)) {
      this.showProfileMenu.set(false);
    }
  }
}
